import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import JSONataEditor from 'react-scale/corp/components/JSONataEditor';
import { useCompassCard } from '@scale/compass-ui/store';
import type { TaskResponseContext } from '@scale/genai-compass-shared/cards/task_response';
import { useState, useEffect } from 'react';
import { useDebounce } from 'react-use-v13';
import {
  Checkbox,
  Flex,
  Box,
  Text,
  FormField,
  TextField,
  Callout,
  Code,
  Em,
  Link,
} from '@scale/scaleui-radix';
import { faBookOpen } from '@fortawesome/pro-solid-svg-icons';
import { CompassInstructions } from '@scale/compass-ui/components/CardInstructions';

const TaskResponseCardComponent: CompassCardComponent = ({ index }: CompassCardComponentProps) => {
  const { card, updateCardContext } = useCompassCard(index);
  const context = card.defaultContext as TaskResponseContext;

  // Local state for the editor
  const [localJsonata, setLocalJsonata] = useState(context.jsonataExpression);
  const [localColumnName, setLocalColumnName] = useState(context.columnName);

  // Debounced effect to update context
  useDebounce(
    () => {
      if (localJsonata !== context.jsonataExpression) {
        updateCardContext({
          ...context,
          jsonataExpression: localJsonata,
          columnName: localColumnName,
        } as TaskResponseContext);
      }
    },
    200,
    [localJsonata],
  );

  useDebounce(
    () => {
      updateCardContext({
        ...context,
        columnName: localColumnName,
      } as TaskResponseContext);
    },
    200,
    [localColumnName],
  );

  // Sync local state when context changes from outside
  useEffect(() => {
    setLocalJsonata(context.jsonataExpression);
    setLocalColumnName(context.columnName);
  }, [context.jsonataExpression, context.columnName]);

  return (
    <div className="flex flex-col gap 4">
      <div className="mb-3">
        <CompassInstructions
          header="How to use the Import Latest Response card"
          storageKey="importLatestResponseCardInstructionsExpanded"
          icon={faBookOpen}
        >
          <Callout.Text>
            <Text as="p">
              <div className="mb-3">
                Use JSONata expressions to transform task responses into your desired table format.
                The transformed data will be added as a new column. Enable flattening to split
                nested responses into separate columns (will only flatten 1 level deep).
              </div>

              <div className="mb-3">
                To test expressions, use the FILTER card before this card to limit to 1 row, then
                run this card with the <Code>$</Code> expression and test in the{' '}
                <Link href="https://try.jsonata.org/">JSONata Playground</Link>. You can also
                retrieve the full response and extract later using the jsons package in Python
                steps.
              </div>

              <div>
                <Em>*Preview mode coming soon</Em>
              </div>
            </Text>
          </Callout.Text>
        </CompassInstructions>
      </div>

      <div className="flex flex-col gap-2 mb-3">
        <FormField.Root size="3">
          <FormField.Label htmlFor="response-column-name">Response Column Name</FormField.Label>
          <TextField.Root
            id="response-column-name"
            className="flex-grow"
            value={localColumnName}
            onChange={e => setLocalColumnName(e.target.value)}
          />
        </FormField.Root>
      </div>

      <div className="flex flex-col gap-2 mb-3">
        <FormField.Root size="3">
          <FormField.Label htmlFor="response-column-name">JSONata Expression</FormField.Label>
          <div className="h-[200px] border border-[#d9deec] rounded-sm">
            <JSONataEditor
              value={localJsonata}
              onChange={setLocalJsonata}
              options={{
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                minimap: { enabled: false },
              }}
            />
          </div>
        </FormField.Root>
      </div>

      <div className="mt-3">
        <Flex align="center" justify="start">
          <Box>
            <Text className="cursor-pointer" as="label" size="3">
              <Flex as="span" gap="2">
                <Checkbox
                  checked={context.flatten}
                  onCheckedChange={() => {
                    updateCardContext({
                      ...context,
                      flatten: !context.flatten,
                    } as TaskResponseContext);
                  }}
                />{' '}
                Flatten the retrieved response into separate columns (Response column name will be
                prefixed)
              </Flex>
            </Text>
          </Box>
        </Flex>
      </div>
    </div>
  );
};

export default TaskResponseCardComponent;
